import React, { useEffect, useState } from 'react';
import { createAlchemyWeb3, Nft } from "@alch/alchemy-web3";
import { useNavigate } from 'react-router';
import axios from 'axios';
import { ethers } from 'ethers';

// ANTD
import { Button } from 'antd';

// STATE
import { useGlobalState } from '../index';

//MODULES
import Error from '../shared/Error';
import Loader from '../shared/Loader';

// STYLES
import './styles.scss';

// CONSTANTS
import { ALCHEMY_ERROR_MSG, BUTTON_ERROR_MSG, ERROR_NAVIGATE_URL, METADATA_ERROR_MSG, RETRIEVING_NFT } from './constants';

// INTERFACES
export interface Signature {
    address: string;
    message: string;
    signature: string;
    chainId: number;
};

export interface NftClaim {
    walletId: string;
    items: {
        tshirts: number[];
        hoodies: number[];
        sneakers: number[];
        platinumSneakers: number[];
    }
};

function Claim() {

    const [state, dispatch] = useGlobalState();
    const [loadingState, setLoadingState] = useState<boolean>(false);
    const [errorState, setErrorState] = useState<boolean>(false);
    const [errorMsgState, setErrorMsgState] = useState<string[]>([]);

    const [tShirtIdsState, setTShirtIdsState] = useState<number[]>([]);
    const [hoodieIdsState, setHoodieIdsState] = useState<number[]>([]);
    const [sneakerIdsState, setSneakerIdsState] = useState<number[]>([]);
    const [platinumIdsState, setPlatinumIdsState] = useState<number[]>([]);

    const navigate = useNavigate();

    const getTokenByAddressAlchemy = async (user: string): Promise<void> => {

        setLoadingState(true);

        const totalNfts: Nft[] = [];

        await getAlchemyNfts(user, totalNfts);
        await checkClaimedTokensAlchemy(totalNfts as Nft[]);

        setLoadingState(false);

    };

    const getAlchemyNfts = async (user: string, totalNfts: Nft[], pageKey?: string): Promise<Nft[] | void> => {
        try {

            const web3 = createAlchemyWeb3(process.env.REACT_APP_ALCHEMY_API as string);
            const nfts = await web3.alchemy.getNfts({ owner: user, contractAddresses: [process.env.REACT_APP_TOKEN_ADDRESS as string], pageKey });

            totalNfts.push(...nfts.ownedNfts);

            if (totalNfts.length < nfts.totalCount) await getAlchemyNfts(user, totalNfts, nfts.pageKey);

        } catch (error: any) {
            setLoadingState(false);
            setErrorState(true);
            setErrorMsgState(ALCHEMY_ERROR_MSG);
        }
    };

    const checkClaimedTokensAlchemy = async (nfts: Nft[]): Promise<void> => {

        const userTokensMetaData: any[] = [];
        const claimedTokenIds: number[] = [];

        const tShirtTokens: number[] = [];
        const hoodieTokens: number[] = [];
        const sneakerTokens: number[] = [];
        const platinumTokens: number[] = [];

        const tShirtClaimedTokens: number[] = [];
        const hoodieClaimedTokens: number[] = [];
        const sneakerClaimedTokens: number[] = [];
        const platinumClaimedTokens: number[] = [];

        const tShirtNonClaimedTokens: number[] = [];
        const hoodieNonClaimedTokens: number[] = [];
        const sneakerNonClaimedTokens: number[] = [];
        const platinumNonClaimedTokens: number[] = [];

        const tokenIds = nfts.map((nft: Nft) => parseInt(nft.id.tokenId, 16));

        try {

            const metadataResponse = await axios.post(`${process.env.REACT_APP_API_URL}/nft/metadata`, tokenIds);

            for (let i = 0; i < metadataResponse.data.length; i++) {

                const nftMetadata = metadataResponse?.data[i].data;

                userTokensMetaData.push(nftMetadata);

                const tokenClaimedStatus = nftMetadata.attributes[1].value;
                const tokenType = nftMetadata.attributes[0].value;
                const tokenId = nftMetadata.tokenId;

                if (tokenClaimedStatus === 'NO') {

                    if (tokenType === 'T-Shirt') tShirtNonClaimedTokens.push(tokenId);
                    if (tokenType === 'Hoodie') hoodieNonClaimedTokens.push(tokenId);
                    if (tokenType === 'Shoes') sneakerNonClaimedTokens.push(tokenId);
                    if (tokenType === 'Platinum Shoes') platinumNonClaimedTokens.push(tokenId);

                } else if (tokenClaimedStatus === 'YES') {

                    claimedTokenIds.push(tokenId);

                    if (tokenType === 'T-Shirt') tShirtClaimedTokens.push(tokenId);
                    if (tokenType === 'Hoodie') hoodieClaimedTokens.push(tokenId);
                    if (tokenType === 'Shoes') sneakerClaimedTokens.push(tokenId);
                    if (tokenType === 'Platinum Shoes') platinumClaimedTokens.push(tokenId);

                }

                if (tokenType === 'T-Shirt') tShirtTokens.push(tokenId);
                if (tokenType === 'Hoodie') hoodieTokens.push(tokenId);
                if (tokenType === 'Shoes') sneakerTokens.push(tokenId);
                if (tokenType === 'Platinum Shoes') platinumTokens.push(tokenId);
            }

            if (tShirtNonClaimedTokens.length + hoodieNonClaimedTokens.length + sneakerNonClaimedTokens.length + platinumNonClaimedTokens.length === 0) {
                navigate('/');
            }

            dispatch({
                claimedTokenIds,
                userTokensMetaData,
                tShirtTokens,
                hoodieTokens,
                sneakerTokens,
                platinumTokens,
                tShirtClaimedTokens,
                hoodieClaimedTokens,
                sneakerClaimedTokens,
                platinumClaimedTokens,
                tShirtNonClaimedTokens,
                hoodieNonClaimedTokens,
                sneakerNonClaimedTokens,
                platinumNonClaimedTokens
            });

        } catch (error: any) {
            setLoadingState(false);
            setErrorState(true);
            setErrorMsgState(METADATA_ERROR_MSG);
        }

    };


    const authenticate = async (signature: Signature): Promise<void> => {
        try {

            setLoadingState(true);

            const token = await axios.post(`${process.env.REACT_APP_API_URL}/auth`, signature);
            localStorage.setItem('token', token.data);

            await claim(token.data);

        } catch (error: any) {
            setLoadingState(false);
            setErrorState(true);
        }
    };

    const signMessage = async (): Promise<void> => {
        try {

            const provider = new ethers.providers.Web3Provider(window.ethereum);
            const signer = provider.getSigner();

            // const message = Math.floor(Math.random() * 1000000).toString();
            // const message = `I am claiming ${tShirtIdsState.length} Shirts, ${hoodieIdsState.length} Hoodies, ${sneakerIdsState.length} Shoes, and ${platinumIdsState.length} Platinum Shoes using a unique URL. Do not share this link with anyone else. Each NFT can only be claimed once.`;

            const message = `Sign this message to agree to UNXD's (terms of service) https://unxd.com/terms and begin the physical redemption process for your DG Realta Parallela NFTs.\n \nYou are starting to claim ${tShirtIdsState.length} T-Shirts, ${hoodieIdsState.length} Hoodies, ${sneakerIdsState.length} Shoes, and ${platinumIdsState.length} Platinum Shoes. All items can only be redeemed once.\n \n You will be redirected to a private store link to complete the redemption process.\n \nFor the security of your order, do not share this link with anyone.`;
            const signature = await signer.signMessage(message);
            const address = await signer.getAddress();

            await authenticate({
                address,
                message,
                signature,
                chainId: state.chainId
            });

        } catch (error: any) { }
    };

    const claim = async (token: string): Promise<void> => {
        try {

            const config = {
                headers: { Authorization: `Bearer ${token}` },
            };

            const payload: NftClaim = {
                walletId: state.user,
                items: { tshirts: tShirtIdsState, hoodies: hoodieIdsState, sneakers: sneakerIdsState, platinumSneakers: platinumIdsState }
            };

            const response = await axios.post(`${process.env.REACT_APP_API_URL}/nft/claim`, payload, config);

            window.location.href = response.data.redirectURL;

        } catch (error: any) {
            console.log(error);
            setErrorState(true);
        }

    };

    const addTShirtAmount = (): void => {
        if (tShirtIdsState?.length < state.tShirtNonClaimedTokens.length) {
            let flag = 0;
            state.tShirtNonClaimedTokens.forEach((tShirtNonClaimedToken: number) => {
                let tShirtIdFound = tShirtIdsState.find((tShirtIdState: number) => tShirtNonClaimedToken === tShirtIdState);
                if (!tShirtIdFound && flag === 0) {
                    setTShirtIdsState([...tShirtIdsState, tShirtNonClaimedToken]);
                    flag = 1;
                }
            });
        }
    };

    const subTShirtAmount = (): void => {
        if (tShirtIdsState?.length > 0) {
            tShirtIdsState.length = tShirtIdsState.length - 1;
            setTShirtIdsState([...tShirtIdsState]);
        }
    };

    const addHoodieAmount = (): void => {
        if (hoodieIdsState?.length < state.hoodieNonClaimedTokens.length) {
            let flag = 0;
            state.hoodieNonClaimedTokens.forEach((hoodieNonClaimedToken: number) => {
                let hoodieIdFound = hoodieIdsState.find((hoodieIdState: number) => hoodieNonClaimedToken === hoodieIdState);
                if (!hoodieIdFound && flag === 0) {
                    setHoodieIdsState([...hoodieIdsState, hoodieNonClaimedToken]);
                    flag = 1;
                }
            });
        }
    };

    const subHoodieAmount = (): void => {
        if (hoodieIdsState?.length > 0) {
            hoodieIdsState.length = hoodieIdsState.length - 1;
            setHoodieIdsState([...hoodieIdsState]);
        }
    };

    const addSneakerAmount = (): void => {
        if (sneakerIdsState?.length < state.sneakerNonClaimedTokens.length) {
            let flag = 0;
            state.sneakerNonClaimedTokens.forEach((sneakerNonClaimedToken: number) => {
                let hoodieIdFound = sneakerIdsState.find((sneakerIdState: number) => sneakerNonClaimedToken === sneakerIdState);
                if (!hoodieIdFound && flag === 0) {
                    setSneakerIdsState([...sneakerIdsState, sneakerNonClaimedToken]);
                    flag = 1;
                }
            });
        }
    };

    const subSneakerAmount = (): void => {
        if (sneakerIdsState?.length > 0) {
            sneakerIdsState.length = sneakerIdsState.length - 1;
            setSneakerIdsState([...sneakerIdsState]);
        }
    };

    const addPlatinumAmount = (): void => {
        if (platinumIdsState?.length < state.platinumNonClaimedTokens.length) {
            let flag = 0;
            state.platinumNonClaimedTokens.forEach((platinumNonClaimedToken: number) => {
                let hoodieIdFound = platinumIdsState.find((platinumIdState: number) => platinumNonClaimedToken === platinumIdState);
                if (!hoodieIdFound && flag === 0) {
                    setPlatinumIdsState([...platinumIdsState, platinumNonClaimedToken]);
                    flag = 1;
                }
            });
        }
    };

    const subPlatinumAmount = (): void => {
        if (platinumIdsState?.length > 0) {
            platinumIdsState.length = platinumIdsState.length - 1;
            setPlatinumIdsState([...platinumIdsState]);
        }
    };

    useEffect(() => {
        state.user && getTokenByAddressAlchemy(state.user);
    }, [state.user]);

    if (loadingState) {
        return (
            <div className="claim-nft-container loading">
                <div className="claim-nft-loader-container">
                    <Loader loaderStatus={RETRIEVING_NFT} />
                </div>
            </div>
        );
    };

    if (errorState) {
        return (
            <div className="claim-nft-container error">
                <div className="claim-nft-error-container">
                    <Error errorStatus={errorMsgState} navigationUrl={ERROR_NAVIGATE_URL} buttonErrorMsg={BUTTON_ERROR_MSG} />
                </div>
            </div>
        );
    }

    return (
        <div className="claim-nft-container">
            <div className="claim-nft-content">
                <div className="claim-nft-header">
                    <ul>
                        <li>Home</li>
                        <li>Claim</li>
                    </ul>
                    <h1>You have {state.userTokensMetaData.length - state.claimedTokenIds.length} unclaimed {(state.userTokensMetaData.length - state.claimedTokenIds.length > 1) ? 'NFTs' : 'NFT'}</h1>
                    <span>You have claimed {state.claimedTokenIds.length} out of {state.userTokensMetaData.length} {state.userTokensMetaData.length !== 1 ? 'NFTs' : 'NFT'} in your Wallet.</span>
                </div>
                <div className="claim-nft-claim-content">
                    <div className="claim-nft-grid-container">
                        {state.tShirtTokens.length ? (
                            <div className="item-container">
                                <video autoPlay={true} poster="https://res.cloudinary.com/dvz8qqgob/image/upload/v1664353719/UNXD/tshirts-1_b0k99x.jpg" preload="none" loop playsInline muted>
                                    <source src="https://res.cloudinary.com/dvz8qqgob/video/upload/q_auto/v1663842042/UNXD/tshirts_wefqxv.mp4" type="video/mp4" />
                                </video>
                                <div className="item-amount">{state.tShirtClaimedTokens.length} of {state.tShirtTokens.length} claimed</div>
                                <div className="item-controller">
                                    <h3>Realtà Parallela T-Shirts</h3>
                                    <div>
                                        <span className="control" onClick={() => subTShirtAmount()}>-</span>
                                        <span className="units">{tShirtIdsState.length}/{state.tShirtNonClaimedTokens.length}</span>
                                        <span className="control" onClick={() => addTShirtAmount()}>+</span>
                                    </div>
                                </div>
                            </div>
                        ) : null}
                        {state.hoodieTokens.length ? (
                            <div className="item-container">
                                <video autoPlay={true} poster="https://res.cloudinary.com/dvz8qqgob/image/upload/v1664353719/UNXD/main-hero2_zmzwfz.jpg" preload="none" loop playsInline muted>
                                    <source src="https://res.cloudinary.com/dvz8qqgob/video/upload/q_auto/v1663842034/UNXD/hoodies_lv2pos.mp4" type="video/mp4" />
                                </video>
                                <div className="item-amount">{state.hoodieClaimedTokens.length} of {state.hoodieTokens.length} claimed</div>
                                <div className="item-controller">
                                    <h3>Dolce & Gabbana Hoodies</h3>
                                    <div>
                                        <span className="control" onClick={() => subHoodieAmount()}>-</span>
                                        <span className="units">{hoodieIdsState.length}/{state.hoodieNonClaimedTokens.length}</span>
                                        <span className="control" onClick={() => addHoodieAmount()}>+</span>
                                    </div>
                                </div>
                            </div>
                        ) : null}
                        {state.sneakerTokens.length ? (
                            <div className="item-container">
                                <video autoPlay={true} poster="https://res.cloudinary.com/dvz8qqgob/image/upload/v1664353719/UNXD/sneakers-1_weuxu1.jpg" preload="none" loop playsInline muted>
                                    <source src="https://res.cloudinary.com/dvz8qqgob/video/upload/q_auto/v1663842009/UNXD/sneakers_etkfc5.mp4" type="video/mp4" />
                                </video>
                                <div className="item-amount">{state.sneakerClaimedTokens.length} of {state.sneakerTokens.length} claimed</div>
                                <div className="item-controller">
                                    <h3>Dolce & Gabbana Sneakers</h3>
                                    <div>
                                        <span className="control" onClick={() => subSneakerAmount()}>-</span>
                                        <span className="units">{sneakerIdsState.length}/{state.sneakerNonClaimedTokens.length}</span>
                                        <span className="control" onClick={() => addSneakerAmount()}>+</span>
                                    </div>
                                </div>
                            </div>
                        ) : null}
                        {state.platinumTokens.length ? (
                            <div className="item-container">
                                <video autoPlay={true} poster="https://res.cloudinary.com/dvz8qqgob/image/upload/v1664353719/UNXD/psneakers-1_rwcu5n.jpg" preload="none" loop playsInline muted>
                                    <source src="https://res.cloudinary.com/dvz8qqgob/video/upload/q_auto/v1663842049/UNXD/D_G_Gems_im94o3.mp4" type="video/mp4" />
                                </video>
                                <div className="item-amount">{state.platinumClaimedTokens.length} of {state.platinumTokens.length} claimed</div>
                                <div className="item-controller">
                                    <h3>Dolce & Gabbana Platinum Sneakers</h3>
                                    <div>
                                        <span className="control" onClick={() => subPlatinumAmount()}>-</span>
                                        <span className="units">{platinumIdsState.length}/{state.platinumNonClaimedTokens.length}</span>
                                        <span className="control" onClick={() => addPlatinumAmount()}>+</span>
                                    </div>
                                </div>
                            </div>
                        ) : null}
                    </div>
                    <div className="claim-content-box">
                        <div className="claim-content-info">
                            <h3>Claiming Process</h3>
                            <p>After selecting the quantity of each item you would like to claim, click the claim button to then select the corresponding style and size for each NFT you’d like to claim.</p>
                            <p><strong>For your security, do not share this link publicly.</strong></p>
                            {/* <p>Add your {state.userTokensMetaData.length - state.claimedTokenIds.length} unclaimed NFTs on your wallet Please select your items to claim your physical products and digital NFTs.</p> */}
                        </div>
                        <div className="claim-list-info">
                            <div>
                                <h3>NFTs Selected for Claiming</h3>
                                <ul>
                                    {state.tShirtTokens.length ? (<li><span>T-shirts</span><span>{tShirtIdsState.length} of {state.tShirtNonClaimedTokens.length}</span></li>) : null}
                                    {state.hoodieTokens.length ? (<li><span>Hoodies</span><span>{hoodieIdsState.length} of {state.hoodieNonClaimedTokens.length}</span></li>) : null}
                                    {state.sneakerTokens.length ? (<li><span>Sneakers</span><span>{sneakerIdsState.length} of {state.sneakerNonClaimedTokens.length}</span></li>) : null}
                                    {state.platinumTokens.length ? (<li><span>Platinum Sneakers</span><span>{platinumIdsState.length} of {state.platinumNonClaimedTokens.length}</span></li>) : null}
                                </ul>
                            </div>
                        </div>
                        <div className="claim-actions">
                            <Button type="primary" shape="round" size="large" onClick={() => signMessage()} disabled={tShirtIdsState.length + hoodieIdsState.length + sneakerIdsState.length + platinumIdsState.length === 0}>Claim ({tShirtIdsState.length + hoodieIdsState.length + sneakerIdsState.length + platinumIdsState.length})</Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Claim;